import React from 'react';
import { formatLongDate } from '../dateOnly';

// "To Do" dashboard widget: compliance items for this therapist's clients —
// clients who turned 18 while still marked minor, and clients whose portal
// consent paperwork is over a year old — plus new-client banners the
// therapist set aside with "Later" (they keep their Review / Create actions
// here until the client is created, reviewed, canceled, or ages out).
// Attention data comes from GET /therapist/client-attention (fetched once by
// the dashboard); new-client items come from the dashboard's prompt lists.

export default function ClientToDoCard({
  data, onOpenProfile,
  newClients = [], onReviewNewClient, onCreateNewClient, creatingAcuityId = null,
}) {
  if (!data) return <div style={mutedStyle}>Loading…</div>;
  const { turned18 = [], staleConsent = [] } = data;
  const total = turned18.length + staleConsent.length + newClients.length;

  if (total === 0) {
    return (
      <div style={{ ...mutedStyle, padding: '10px 4px' }}>
        ✅ All caught up — nothing waiting on you.
      </div>
    );
  }

  return (
    <div>
      <div style={{ ...mutedStyle, marginBottom: 8 }}>
        {total} item{total === 1 ? '' : 's'} · these clear on their own once the profile, paperwork, or new client is taken care of.
      </div>

      {newClients.map((p) => {
        const name = `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'New client';
        const busy = creatingAcuityId === p.acuityId;
        const isConfirmed = p.kind === 'confirmed';
        return (
          <div
            key={`n-${p.kind}-${p.acuityId}`}
            style={{
              ...rowStyle,
              background: isConfirmed ? '#e7f6ec' : '#e8edf8',
              border: `1px solid ${isConfirmed ? '#bfe5cc' : '#c9d3ee'}`,
            }}
          >
            <div style={{ flex: 1 }}>
              {isConfirmed ? '✅' : '🆕'} <strong>{name}</strong>
              {isConfirmed
                ? ' — office confirmed; create their profile and send the welcome letter.'
                : ` — new client request${p.apptDatetime ? ` for ${fmtAppt(p.apptDatetime)}` : ''}; review or create their profile.`}
              {isConfirmed && p.officeNote ? <span style={mutedStyle}> Office note: {p.officeNote}</span> : null}
            </div>
            {!isConfirmed && onReviewNewClient && (
              <button type="button" style={ghostBtnStyle} onClick={() => onReviewNewClient(p)} disabled={busy}>Review</button>
            )}
            {onCreateNewClient && (
              <button type="button" style={btnStyle} onClick={() => onCreateNewClient(p)} disabled={busy}>
                {busy ? 'Loading…' : 'Create client'}
              </button>
            )}
          </div>
        );
      })}

      {turned18.map((c) => (
        <div key={`a-${c.id}`} style={{ ...rowStyle, background: '#fde8e8', border: '1px solid #f5c2c2' }}>
          <div style={{ flex: 1 }}>
            🔞 <strong>{c.client_name}</strong> is now 18 but still marked as a minor —
            update signatures and ROI if needed, then un-check "Is minor" on their profile.
          </div>
          <button type="button" style={btnStyle} onClick={() => onOpenProfile(c.id)}>Open profile</button>
        </div>
      ))}

      {staleConsent.map((c) => (
        <div key={`s-${c.id}`} style={{ ...rowStyle, background: '#fff3cd', border: '1px solid #ffe69c' }}>
          <div style={{ flex: 1 }}>
            📄 <strong>{c.client_name}</strong>'s consent paperwork is over a year old
            (last signed {fmtTimestamp(c.last_consent)}) — send a new intake link to refresh it.
          </div>
          <button type="button" style={btnStyle} onClick={() => onOpenProfile(c.id)}>Open profile</button>
        </div>
      ))}
    </div>
  );
}

// last_consent is a real timestamp (created_at), so a plain Date is correct
// here; only date-only columns need the local-parse treatment.
function fmtTimestamp(ts) {
  if (!ts) return '';
  const dt = new Date(ts);
  if (isNaN(dt)) return '';
  // A date-only string sneaking through would still shift — route it through
  // the safe formatter.
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(ts))) return formatLongDate(ts);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Appointment times are real timestamps (Acuity), so local rendering is right.
function fmtAppt(ts) {
  const dt = new Date(ts);
  return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

const mutedStyle = { color: 'var(--text-muted)', fontSize: 13 };
const rowStyle = {
  display: 'flex', gap: 10, alignItems: 'center',
  borderRadius: 8, padding: '8px 12px', margin: '6px 0', fontSize: 13.5, lineHeight: 1.4,
};
const btnStyle = {
  flexShrink: 0, background: 'var(--primary-color)', color: '#fff', border: 'none',
  borderRadius: 6, padding: '6px 12px', fontWeight: 700, fontSize: 12.5, cursor: 'pointer',
};
const ghostBtnStyle = {
  ...btnStyle, background: 'transparent', color: 'var(--primary-color)', border: '1px solid var(--primary-color)',
};
